import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export const showToast = (title, icon = "success") => {
  Swal.fire({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title,
  });
};

const LimitsForm = ({ eventId }) => {
  const timeOptions = Array.from({ length: 24 }, (_, i) => (i + 1) * 5);
  const [formData, setFormData] = useState({
    limit_booking_frequency: false,
    only_first_slot: false,
    limit_total_duration: false,
    limit_future_bookings: false,
    periodDays: "calendar",
    always_30_days_available: false,
    date_range_start: "",
    date_range_end: "",
    slot_interval: 0,
    total_duration: [],
    booking_frequency: [],
    limit_frequency_value: 1,
    max_booking_per_slot: 1, // Default minimum 1
  });

  useEffect(() => {
    const fetchEventLimits = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/events/${eventId}/limits`,
          { withCredentials: true }
        );
        const limits = response.data;

        setFormData({
          limit_booking_frequency: limits.limit_booking_frequency === 1,
          only_first_slot: limits.only_first_slot === 1,
          limit_total_duration: limits.limit_total_duration === 1,
          limit_future_bookings: limits.limit_future_bookings === 1,
          periodDays: limits.period_days || "calendar",
          always_30_days_available: limits.always_30_days_available === 1,
          date_range_start: limits.date_range_start || "",
          date_range_end: limits.date_range_end || "",
          slot_interval: limits.slot_interval || 0,
          total_duration: (limits.total_duration || []).map((d) => ({
            value: d.total_duration_value,
            unit: d.total_duration_unit,
          })),
          booking_frequency: (limits.booking_frequency || []).map((d) => ({
            value: d.total_frequency_value,
            unit: d.total_frequency_unit,
          })),
          limit_frequency_value: 1,
          max_booking_per_slot: limits.max_booking_per_slot || 1,
        });
      } catch (err) {
        console.error("Error fetching event limits:", err);
      }
    };
    fetchEventLimits();
  }, [eventId]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const data = Object.fromEntries(form.entries());

    data.limit_booking_frequency = form.get("limit_booking_frequency") ? 1 : 0;
    data.only_first_slot = form.get("only_first_slot") ? 1 : 0;
    data.limit_total_duration = form.get("limit_total_duration") ? 1 : 0;
    data.limit_future_bookings = form.get("limit_future_bookings") ? 1 : 0;
    data.always_30_days_available = form.get("always_30_days_available")
      ? 1
      : 0;
    data.periodDays = form.get("periodDays") || "calendar";
    data.slot_interval = parseInt(form.get("slot_interval") || "0", 10);
    data.date_range_start = form.get("date_range_start");
    data.date_range_end = form.get("date_range_end");
    data.max_booking_per_slot =
      parseInt(form.get("max_booking_per_slot"), 10) || 1;
    data.total_duration = form
      .getAll("total_duration_value[]")
      .map((value, index) => ({
        total_duration_value: parseInt(value, 10),
        total_duration_unit: form.getAll("total_duration[]")[index],
      }))
      .filter((d) => !isNaN(d.total_duration_value) && d.total_duration_unit);

    data.booking_frequency = form
      .getAll("limit_frequency_value[]")
      .map((value, index) => ({
        total_frequency_value: parseInt(value, 10),
        total_frequency_unit: form.getAll("booking_frequency[]")[index],
      }))
      .filter((d) => !isNaN(d.total_frequency_value) && d.total_frequency_unit);

    try {
      await axios.put(
        `${API_BASE_URL}/events/${eventId}/limits`,
        data,
        { withCredentials: true }
      );
      showToast("Limits updated successfully!");
    } catch (err) {
      console.error("Error saving limits", err);
    }
  };

  // ---------- Render Helpers ----------
  const renderTimeSelect = (name, label) => (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <select className="form-select" name={name}>
        <option value="0">No buffer time</option>
        {timeOptions.map((val) => (
          <option key={val} value={val}>
            {val} minutes
          </option>
        ))}
      </select>
    </div>
  );

  const renderMinNoticeSelect = () => (
    <div className="mb-3">
      <label className="form-label">Minimum Notice</label>
      <div className="input-group">
        <input
          type="number"
          min="0"
          className="form-control"
          name="minimum_notice_value"
          defaultValue="2"
        />
        <select
          className="form-select"
          name="minimum_notice_unit"
          defaultValue="hours"
        >
          <option value="minutes">Minutes</option>
          <option value="hours">Hours</option>
          <option value="days">Days</option>
        </select>
      </div>
    </div>
  );

  const renderSlotIntervalSelect = () => (
    <div className="mb-3">
      <label className="form-label">Time-slot Intervals</label>
      <select
        className="form-select"
        name="slot_interval"
        value={formData.slot_interval}
        onChange={handleChange}
      >
        <option value="0">Use event length (default)</option>
        {timeOptions.map((val) => (
          <option key={val} value={val}>
            {val} minutes
          </option>
        ))}
      </select>
    </div>
  );

  const renderDurationFields = () => (
    <>
      {formData.total_duration.map((duration, index) => (
        <div key={index} className="d-flex gap-2 align-items-center mb-2 mw304">
          <input
            type="number"
            className="form-control mr-2"
            value={duration.value}
            name="total_duration_value[]"
            onChange={(e) => handleDurationChange(e, index, "value")}
          />
          <select
            name="total_duration[]"
            className="form-select mr-2"
            value={duration.unit}
            onChange={(e) => handleDurationChange(e, index, "unit")}
          >
            <option value="minute">Minutes</option>
            <option value="hour">Hours</option>
            <option value="day">Days</option>
            <option value="week">Weeks</option>
            <option value="month">Months</option>
          </select>
          <button
            type="button"
            onClick={() => handleRemoveDuration(index)}
            className="btn btn-danger ml-2"
          >
            <i class="bi bi-trash-fill"></i>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={handleAddDuration}
        className="btn btn-success mt-2"
      >
        Add Duration
      </button>
    </>
  );

  const renderFrequencyFields = () => (
    <div>
      {formData.booking_frequency.map((frequency, index) => (
        <div
          key={index}
          className="d-flex align-items-center mb-2"
          style={{ maxWidth: 300 }}
        >
          <input
            type="number"
            className="form-control mr-2"
            name="limit_frequency_value[]"
            value={frequency.value}
            onChange={(e) => {
              const updated = [...formData.booking_frequency];
              updated[index].value = e.target.value;
              setFormData({ ...formData, booking_frequency: updated });
            }}
          />
          <span className="px-2">per</span>
          <select
            name="booking_frequency[]"
            className="form-select mr-2"
            value={frequency.unit}
            onChange={(e) => {
              const updated = [...formData.booking_frequency];
              updated[index].unit = e.target.value;
              setFormData({ ...formData, booking_frequency: updated });
            }}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
          <button
            type="button"
            onClick={() => {
              const updated = formData.booking_frequency.filter(
                (_, i) => i !== index
              );
              setFormData({ ...formData, booking_frequency: updated });
            }}
            className="btn btn-danger ms-2"
          >
            <i className="bi bi-trash-fill"></i>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          setFormData({
            ...formData,
            booking_frequency: [
              ...formData.booking_frequency,
              { value: formData.limit_frequency_value, unit: "day" },
            ],
          })
        }
        className="btn btn-success mt-2"
      >
        Add Frequency
      </button>
    </div>
  );

  const handleAddDuration = () => {
    setFormData({
      ...formData,
      total_duration: [
        ...formData.total_duration,
        { value: 60, unit: "minute" },
      ],
    });
  };

  const handleRemoveDuration = (index) => {
    const updated = formData.total_duration.filter((_, i) => i !== index);
    setFormData({ ...formData, total_duration: updated });
  };

  const handleDurationChange = (e, index, field) => {
    const updated = formData.total_duration.map((d, i) =>
      i === index ? { ...d, [field]: e.target.value } : d
    );
    setFormData({ ...formData, total_duration: updated });
  };

  const renderToggleSection = ({ name, label, content }) => (
    <div className="form-check form-switch mb-3 custom-switch">
      <input
        className="form-check-input"
        type="checkbox"
        name={name}
        id={name}
        checked={formData[name]}
        onChange={handleChange}
      />
      <label className="form-check-label" htmlFor={name}>
        {label}
      </label>
      {formData[name] && <div className="mt-2">{content}</div>}
    </div>
  );

  return (
    <form id="limitsForm" className="mt-0" onSubmit={handleSubmit}>
      <div className="title-line2">
        <h5 class="mb-0 fw700">Limits</h5>
      </div>
      <div className="row">
        <div className="col-md-6 col-12">
          {renderTimeSelect("buffer_before", "Before Event")}
        </div>
        <div className="col-md-6 col-12">
          {renderTimeSelect("buffer_after", "After Event")}
        </div>
        <div className="col-md-6 col-12">{renderMinNoticeSelect()}</div>
        <div className="col-md-6 col-12">{renderSlotIntervalSelect()}</div>
        <div className="col-md-6 col-12">
          <div className="mb-3">
            <label className="form-label">Max Bookings Per Slot</label>
            <input
              type="number"
              name="max_booking_per_slot"
              min="1"
              value={formData.max_booking_per_slot}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      {renderToggleSection({
        name: "limit_booking_frequency",
        label: "Limit how many times this event can be booked",
        content: renderFrequencyFields(),
      })}
      {renderToggleSection({
        name: "only_first_slot",
        label: "Only show the first slot of each day as available",
      })}
      {renderToggleSection({
        name: "limit_total_duration",
        label: "Limit total booking duration",
        content: renderDurationFields(),
      })}

      {renderToggleSection({
        name: "limit_future_bookings",
        label: "Limit future bookings",
        content: (
          <>
            <div className="d-flex mb-2 gap-1 flex-column">
              <div className="form-check mr-4">
                <input
                  type="radio"
                  name="periodDays"
                  value="business"
                  checked={formData.periodDays === "business"}
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">30 Business Days</label>
              </div>
              <div className="form-check">
                <input
                  type="radio"
                  name="periodDays"
                  value="calendar"
                  checked={formData.periodDays === "calendar"}
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">30 Calendar Days</label>
              </div>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                name="always_30_days_available"
                checked={formData.always_30_days_available}
                onChange={handleChange}
                className="form-check-input"
              />
              <label className="form-check-label">
                Always 30 days available
              </label>
            </div>
            <div className="form-group mt-2">
              <label>Date Range</label>
              <div
                className="d-flex align-items-center mb-2"
                style={{ maxWidth: 300 }}
              >
                <input
                  type="date"
                  name="date_range_start"
                  value={formData.date_range_start}
                  onChange={handleChange}
                  className="form-control mr-2"
                />
                <span className="px-2">to</span>
                <input
                  type="date"
                  name="date_range_end"
                  value={formData.date_range_end}
                  onChange={handleChange}
                  className="form-control ml-2"
                />
              </div>
            </div>
          </>
        ),
      })}

      <button type="submit" className="btn btn-success mb-4">
        Save Changes
      </button>
    </form>
  );
};

export default LimitsForm;
